import ToolStylus from './ToolStylus';
import Shortcut from '../utils/Shortcut';
import { type IToolActionInput } from './Tool';
import { calculateDuplicationOffset } from '../utils/duplicationOffset';
import { zoomOutToFit, panTo } from '../utils/zoom';
import type FrameBehaviors from '../objects/FrameBehaviors';
import { hasFrameMemberBehaviors, hasLayout } from '../objects/guards';
import type WhiteboardController from '../controllers/WhiteboardController';

const getParentFrame = (
  controller: WhiteboardController,
  objects: fabric.Object[]
): (fabric.Object & FrameBehaviors) | undefined => {
  if (!objects.every(obj => hasFrameMemberBehaviors(obj))) {
    return undefined;
  }

  const container = objects[0].container;

  if (!objects.every(obj => obj.container === container)) {
    return undefined;
  }

  return controller.canvas.findFirstObject(
    (object): object is fabric.Object & FrameBehaviors =>
      object.id === container
  );
};

class ToolDuplicate extends ToolStylus {
  static toolName = 'Duplicate objects';
  static shortcut = new Shortcut('Cmd+D');

  static action({ controller, selection }: IToolActionInput) {
    if (!selection) return;
    const selectedObjects = controller.canvas.getActiveObjects();

    const parentFrame = getParentFrame(controller, selectedObjects);

    const otherObjects = controller.canvas
      .getObjects()
      .filter(
        obj => !selectedObjects.includes(obj) && obj.id !== parentFrame?.id
      );

    const { offsetX, offsetY } = calculateDuplicationOffset(
      selectedObjects,
      otherObjects,
      parentFrame?.getBoundingRect(true)
    );
    controller.canvas.duplicateActiveSelection(duplicatedObjects => {
      // Filter out group members - only apply offset to top-level objects
      const topLevelObjects = duplicatedObjects.filter(obj => !obj.simpleGroup);

      topLevelObjects.forEach(duplicatedObject => {
        duplicatedObject.moveToCoords({
          left: duplicatedObject.left + offsetX,
          top: duplicatedObject.top + offsetY,
        });
      });
    });

    // At this point, the active object should be the newly duplicated objects.
    const activeObject = controller.canvas.getActiveObject();

    // Try to zoom out to contain the object(s). If that doens't work, then pan to
    // fit the duplicated object(s)
    const didZoom = zoomOutToFit(controller, activeObject);
    if (!didZoom) {
      panTo(controller, activeObject, { animate: false });
    }

    // Remove duplicated objects that might be outside frame. Only applies to non-
    // layout frames
    if (parentFrame && !hasLayout(parentFrame)) {
      controller.canvas.getActiveObjects().forEach(obj => {
        if (!parentFrame.containsObject(obj)) {
          parentFrame.removeMember(obj);
        }
      });
    }

    this.toolActionComplete({ controller });
  }
}

export default ToolDuplicate;
